import type { Duck } from '../entities/Duck';
import { SPLIT_END, SPLIT_START, type River } from './River';
import { DUCK_RADIUS } from './tuning';

export type Route = 'shortcut' | 'safe';

/** Sideways speed (m/s) a planning pilot reckons it can cross the river at. */
const PLAN_CROSS_SPEED = 2.2;

/** Something round to steer around: a rock, or a point along a log. */
export type Obstacle = { x: number; d: number; r: number };

/**
 * Where a pilot wants to be `look` metres ahead: the fast lane, except in the
 * split where a 'safe' pilot commits to the calm left channel early.
 */
export function routeTargetX(duck: Duck, river: River, route: Route, look: number): number {
  const d = duck.distance;
  // Already past the island's nose on the shortcut side: too late for the safe channel.
  const islandHere = river.islandX(d);
  const missedSafe = islandHere !== null && duck.position.x > islandHere;
  if (route === 'safe' && !missedSafe && d > SPLIT_START - 8 && d < SPLIT_END) {
    const at = Math.max(d + look, SPLIT_START + 3);
    const shore = (river.islandX(at) ?? river.centerX(at)) - river.islandHalfWidth(at);
    const bank = river.xAt(at, -1);
    return (shore + bank) / 2;
  }
  return river.laneX(d + look);
}

/**
 * Steer toward targetX, going around any rock (or `extra` obstacle) within
 * `avoidAhead` metres instead of into it. Returns -1, 0 or 1 like a held key.
 */
export function steerToward(
  duck: Duck,
  river: River,
  targetX: number,
  deadband = 0.3,
  avoidAhead = 4,
  extra: readonly Obstacle[] = [],
  plan = false,
): number {
  const speed = -duck.velocity.z;
  for (const rock of [...river.rocks, ...extra]) {
    const ahead = rock.d - duck.distance;
    if (ahead < -0.5 || ahead > avoidAhead) continue;
    const clear = rock.r + DUCK_RADIUS + 0.45;
    if (Math.abs(targetX - rock.x) < clear) {
      let side = targetX >= rock.x ? 1 : -1;
      // A planning pilot at speed stays on its own side if it can't get across
      // in time. Slow, it has time to go around (and holding a side could pin it).
      const mySide = duck.position.x >= rock.x ? 1 : -1;
      if (plan && speed > 3 && side !== mySide) {
        const across = Math.abs(rock.x + side * clear - duck.position.x);
        if (across > (Math.max(0, ahead) / speed) * PLAN_CROSS_SPEED) side = mySide;
      }
      targetX = rock.x + side * clear;
    }
  }
  const offset = duck.position.x - targetX;
  return offset > deadband ? -1 : offset < -deadband ? 1 : 0;
}

/** A rock sits within `ahead` metres downstream, close to the duck's line. */
export function rockAhead(duck: Duck, river: River, ahead: number): boolean {
  const d = duck.distance;
  return river.rocks.some((rock) => rock.d - d > 0 && rock.d - d < ahead && Math.abs(rock.x - duck.position.x) < rock.r + 1);
}

/**
 * Ducks bump each other: push overlapping pairs apart and trade the normal
 * part of their velocities. Returns the hardest impact that involved ducks[0].
 */
export function resolveDuckBumps(ducks: Duck[]): number {
  let playerImpact = 0;
  const minDist = DUCK_RADIUS * 2 * 0.95;
  for (let i = 0; i < ducks.length; i += 1) {
    for (let j = i + 1; j < ducks.length; j += 1) {
      const a = ducks[i];
      const b = ducks[j];
      if (Math.abs(a.position.y - b.position.y) > 0.5) continue;
      const dx = a.position.x - b.position.x;
      const dz = a.position.z - b.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist >= minDist || dist < 1e-4) continue;
      const nx = dx / dist;
      const nz = dz / dist;
      const push = (minDist - dist) / 2;
      a.position.x += nx * push;
      a.position.z += nz * push;
      b.position.x -= nx * push;
      b.position.z -= nz * push;
      const vn = (a.velocity.x - b.velocity.x) * nx + (a.velocity.z - b.velocity.z) * nz;
      if (vn < 0) {
        // Equal masses, a little bouncy.
        const impulse = (-(1 + 0.6) * vn) / 2;
        a.velocity.x += nx * impulse;
        a.velocity.z += nz * impulse;
        b.velocity.x -= nx * impulse;
        b.velocity.z -= nz * impulse;
        if (i === 0) playerImpact = Math.max(playerImpact, -vn);
      }
    }
  }
  return playerImpact;
}
